/**
 * Chess Deck Design System — JS-context color/token accessor.
 *
 * theme.css is the SINGLE SOURCE OF TRUTH for token values. This file does NOT hold its own copies;
 * it reads them from tokens.generated.ts (auto-generated from theme.css by scripts/gen-tokens.cjs,
 * run on prestart/prebuild). So Recharts props, board squares, Clerk theming, and inline styles —
 * the JS contexts that can't use CSS var() — stay in lockstep with theme.css and cannot drift.
 *
 * Change a color value in theme.css, NOT here. The `colors` object below only defines the SHAPE
 * (which token maps to which semantic name); values flow from the generated tokens.
 */
import { tokens as T } from "./tokens.generated";

// ============================================================================
// COLORS — shape only; values sourced from theme.css via tokens.generated.ts
// ============================================================================

export const colors = {
  // Backgrounds
  bg: {
    base: T["--color-bg-base"],
    surface: T["--color-bg-surface"],
    elevated: T["--color-bg-elevated"],
    hover: T["--color-bg-hover"],
    inset: T["--color-bg-inset"],
  },

  // Borders
  border: {
    default: T["--color-border"],
    muted: T["--card-border"],
    hover: T["--color-border-hover"],
  },

  // Text — 4 levels + on-accent
  text: {
    primary: T["--color-text-primary"],
    secondary: T["--color-text-secondary"],
    tertiary: T["--color-text-tertiary"],
    disabled: T["--color-text-disabled"],
    onAccent: T["--color-text-on-accent"],
  },

  // Accent (GitHub blue, dark mode canonical)
  accent: {
    default: T["--color-accent"],
    hover: T["--color-accent-hover"],
    muted: T["--color-accent-muted"],
    text: T["--color-accent-text"],
  },

  // Functional
  success: { default: T["--color-success"], muted: T["--color-success-muted"] },
  warning: { default: T["--color-warning"], muted: T["--color-warning-muted"] },
  error: { default: T["--color-error"], muted: T["--color-error-muted"] },

  // Move classifications — PRIMARY palette (dots, badges, board tints all use these via --cls-*).
  classification: {
    brilliant:  { fg: T["--cls-brilliant"], border: T["--cls-brilliant-border"] },
    great:      { fg: T["--cls-great"], border: T["--cls-great-border"] },
    excellent:  { fg: T["--cls-excellent"], border: T["--cls-excellent-border"] },
    good:       { fg: T["--cls-good"], border: T["--cls-good-border"] },
    opening:    { fg: T["--cls-opening"], border: T["--cls-opening-border"] },
    forced:     { fg: T["--cls-forced"], border: T["--cls-forced-border"] },
    inaccuracy: { fg: T["--cls-inaccuracy"], border: T["--cls-inaccuracy-border"] },
    mistake:    { fg: T["--cls-mistake"], border: T["--cls-mistake-border"] },
    blunder:    { fg: T["--cls-blunder"], border: T["--cls-blunder-border"] },
  },

  // Board
  board: { dark: T["--board-dark"], light: T["--board-light"] },

  // Eval bar
  eval: { white: T["--eval-white"], black: T["--eval-black"] },

  // Accuracy tiers
  accuracy: {
    high: T["--accuracy-high"],
    good: T["--accuracy-good"],
    mid: T["--accuracy-mid"],
    low: T["--accuracy-low"],
    neutral: T["--accuracy-neutral"],
  },

  // Win-rate ramp (openings) — its own concept, see theme.css. Use via `wrColor()`.
  wr: {
    win: T["--wr-win"],
    even: T["--wr-even"],
    loss: T["--wr-loss"],
  },

  // Piece color indicators (Library card dot)
  piece: { white: T["--piece-white"], black: T["--piece-black"] },

  // ── theme.ts-only values (no theme.css token; not duplicated, so no drift risk) ──

  // Chat / Coach tab — bespoke translucent player/opponent tints
  chat: {
    playerBg: 'rgba(79, 172, 254, 0.06)',
    playerBorder: 'rgba(79, 172, 254, 0.15)',
    playerAccent: 'rgba(79, 172, 254, 0.9)',
    opponentBg: 'rgba(255, 180, 80, 0.06)',
    opponentBorder: 'rgba(255, 180, 80, 0.15)',
    opponentAccent: 'rgba(255, 180, 80, 0.9)',
    userBubbleBg: 'rgba(79, 172, 254, 0.12)',
    userBubbleBorder: 'rgba(79, 172, 254, 0.2)',
    assistantBubbleBg: 'rgba(255, 255, 255, 0.04)',
    assistantBubbleBorder: 'rgba(255, 255, 255, 0.08)',
    inputBg: 'rgba(255, 255, 255, 0.05)',
    inputBorder: 'rgba(255, 255, 255, 0.15)',
    chipBg: 'rgba(79, 172, 254, 0.08)',
    chipBorder: 'rgba(79, 172, 254, 0.18)',
  },

  // Grade letter colors (could move to theme.css later; theme.ts-only for now)
  grade: {
    'A+': '#22c55e',
    'A':  '#4ade80',
    'B':  '#eab308',
    'C':  '#f97316',
    'D':  '#ef4444',
  } as Record<string, string>,

  // Drill/tag category colors (theme.ts-only)
  category: {
    tactics:    '#ef4444',
    quiet:      '#a855f7',
    defense:    '#3b82f6',
    opening:    '#22c55e',
    winning:    '#eab308',
    positional: '#f97316',
    endgame:    '#06b6d4',
    psych:      '#8b949e',
  },
} as const;

// ============================================================================
// TYPOGRAPHY
// ============================================================================

export const fonts = {
  sans: "'DM Sans', -apple-system, BlinkMacSystemFont, sans-serif",
  display: "'Lora', Georgia, serif",
  mono: "'JetBrains Mono', 'Fira Code', 'Consolas', monospace",
} as const;

export const fontSizes = {
  xs: '11px',
  sm: '13px',
  base: '15px',
  lg: '18px',
  xl: '22px',
  '2xl': '28px',
  '3xl': '36px',
  '4xl': '48px',
} as const;

export const fontWeights = {
  regular: 400,
  medium: 500,
  semibold: 600,
  bold: 700,
} as const;

export const lineHeights = {
  tight: 1.2,
  normal: 1.5,
  relaxed: 1.6,
} as const;

export const letterSpacing = {
  tight: '-0.02em',
  normal: '-0.01em',
  wide: '0.01em',
} as const;

// ============================================================================
// SPACING (4px grid)
// ============================================================================

// Mirrors theme.css --space-* (the source of truth).
export const spacing = {
  1: '4px',
  2: '8px',
  3: '12px',
  4: '16px',
  5: '20px',
  6: '24px',
  8: '32px',
  12: '48px',
  16: '64px',
} as const;

// ============================================================================
// RADII
// ============================================================================

// Mirrors theme.css --radius-* (the source of truth). 6px interactive / 6px medium / 12px container.
export const radii = {
  sm: '6px',
  md: '6px',
  lg: '12px',
} as const;

// ============================================================================
// TRANSITIONS
// ============================================================================

export const transitions = {
  fast: '150ms ease',
} as const;

// ============================================================================
// STALE TIMES (React Query cache durations)
// ============================================================================

export const staleTimes = {
  /** User's games, profile, dashboard — 10 minutes */
  standard: 10 * 60 * 1000,
  /** A player's newest games on Lichess — 1 minute, so a game just played shows up on the next visit or tab focus.
   *  Chess.com's list is re-read on every visit and focus, and every 20 s while visible, regardless
   *  (useUnanalyzedGames, #344) */
  platformGames: 60 * 1000,
  /** Showcase players — never auto-refetch (hardcoded data) */
  showcase: Infinity,
  /** User profile (users/me) — until sign-out */
  userProfile: Infinity,
} as const;

// ============================================================================
// CLERK APPEARANCE — derived from design tokens
// ============================================================================

export const clerkAppearance = {
  variables: {
    colorPrimary: colors.accent.default,
    colorBackground: colors.bg.surface,
    colorNeutral: colors.text.secondary,
    colorForeground: colors.text.primary,
    colorDanger: colors.error.default,
    colorSuccess: colors.success.default,
    colorWarning: colors.warning.default,
    colorInputForeground: colors.text.primary,
    colorInput: colors.bg.inset,
    colorBorder: colors.border.default,
    colorModalBackdrop: 'rgba(2, 6, 23, 0.8)',
    borderRadius: radii.md,
    fontFamily: fonts.sans,
  },
  elements: {
    card: {
      backgroundColor: colors.bg.surface,
      border: `1px solid ${colors.border.default}`,
      boxShadow: '0 16px 48px rgba(0, 0, 0, 0.4)',
    },
    modalBackdrop: {
      backdropFilter: 'blur(4px)',
    },
  },
} as const;

// ============================================================================
// CONVENIENCE: flat color map for inline styles
// ============================================================================

// ============================================================================
// DERIVED HELPERS
// ============================================================================

/**
 * Win-rate color — the SINGLE source for win% → color across the openings page (tree, family bars,
 * lines-to-practice). Symmetric ramp: win (≥55%), loss (≤45%), even otherwise.
 * Was previously a local `barColor` in the sidebar on different thresholds + palette (success/warning/
 * error) that mismatched the tree — unified here on dedicated --wr-* tokens. (Sam, 2026-06-14.)
 */
/**
 * POPULARITY colour — share is a magnitude, not a good/bad judgement, so it is blue, never the win/loss
 * red-green: a 3% opening isn't "bad", it's rare. (The three-step `popColor` it replaced went with the
 * Analyze tab's old explorer panels, #264.)
 *
 * Share of games where the blue ramp saturates. FIXED, not per-list.
 *
 * Two versions of this were wrong before landing on a fixed ceiling below 100:
 *   - popColor's 25/10 STEPS: at band 1800 the most-played opening after 1.e4 is the Sicilian at
 *     24.9%, so nothing ever hit the top blue and 17 of 20 openings rendered identical grey.
 *   - scaling to each list's own MAX: made 1.d4's 11.8% peak look exactly as popular as the Sicilian's
 *     24.9%, so the colour stopped being comparable between moves (Sam: "Maybe this doesnt work based
 *     on highest. Maybe like 25% is highest/most deep blue across the board?").
 * A fixed 25% ceiling keeps one meaning everywhere — "a quarter of this move's games" is peak blue —
 * while still being reachable, which a 100% ceiling never was for opening shares.
 */
export const POP_SATURATION_PCT = 25;

/**
 * Continuous blue popularity ramp: 0% is muted grey, POP_SATURATION_PCT and above is full accent blue.
 *
 * sqrt, not linear. Shares are heavily skewed (Sicilian 24.9%, median ~2%), so a linear ramp would
 * leave everything below the top two or three visually identical — the same failure as the old steps.
 */
export const popColorScaled = (sharePct: number, ceiling: number = POP_SATURATION_PCT): string => {
  if (!(ceiling > 0)) return "var(--color-text-secondary)";
  const t = Math.max(0, Math.min(1, Math.sqrt(Math.max(0, sharePct) / ceiling)));
  // Interpolate the accent blue toward the muted text grey. Both ends come from theme.css tokens, so
  // this can't drift from the palette.
  return `color-mix(in srgb, ${T["--color-accent"]} ${Math.round(t * 100)}%, ${T["--color-text-tertiary"]})`;
};

/**
 * Win-rate colour. Green from 53, red at 47 or below, yellow between.
 *
 * Was 55/45, which read wrong on the openings page (Sam: "53% and 54% should start being light green
 * not yellow"). The corpus is close-rated, so a band's baseline sits at ~50-51% and the whole spread is
 * roughly 45-57 — a +3pt edge over the field IS the signal there, and painting it the same yellow as
 * a dead-even 50% flattened the thing the page exists to show. Symmetric on the losing side so the
 * scale stays honest in both directions.
 */
export const wrColor = (wr: number): string =>
  wr >= 53 ? colors.wr.win : wr <= 47 ? colors.wr.loss : colors.wr.even;

/**
 * Per-move win% colour: the Maia move table's MAIA WIN / YOUR WIN number. Green at 55 and up, red at 45
 * and below, the table's neutral number colour (secondary text) between.
 *
 * Not wrColor. wrColor's 53/47 and its yellow are tuned for corpus win rates between close-rated players,
 * which all sit around 45-57, so a 3-point edge is the signal there. A per-move Maia win spans roughly
 * 25-75, where 53 against 47 is noise; this is the table's pre-#165 rule (Sam, #199). The green and red
 * are --color-success / --color-error, so a green win matches the green best-move SAN beside it.
 */
export const moveWinColor = (win: number): string =>
  win >= 55 ? colors.success.default : win <= 45 ? colors.error.default : colors.text.secondary;

/** Quick access to most-used colors for inline styles */
export const c = {
  bgBase: colors.bg.base,
  bgSurface: colors.bg.surface,
  bgElevated: colors.bg.elevated,
  bgHover: colors.bg.hover,
  bgInset: colors.bg.inset,
  border: colors.border.default,
  borderMuted: colors.border.muted,
  borderHover: colors.border.hover,
  textPrimary: colors.text.primary,
  textSecondary: colors.text.secondary,
  textTertiary: colors.text.tertiary,
  textDisabled: colors.text.disabled,
  textOnAccent: colors.text.onAccent,
  accent: colors.accent.default,
  accentHover: colors.accent.hover,
  accentMuted: colors.accent.muted,
  accentText: colors.accent.text,
  success: colors.success.default,
  warning: colors.warning.default,
  error: colors.error.default,
} as const;